import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, School, Star, TrendingUp, MoreHorizontal,
  MessageSquare, BookOpen, ClipboardList, AlertTriangle, Users,
  Settings, Database, Cpu, ChevronDown, LogOut, KeyRound, X,
} from 'lucide-react';

import { hapticLight } from '../hooks/useHaptic';
import type { PageId } from '../App';

interface MobileAppNavProps {
  currentPage: PageId;
  onNavigate: (page: PageId) => void;
  /** Page title mapping, defaults provided */
  pageTitles?: Record<string, string>;
  /** Current student name for header display */
  studentName?: string;
  /** User display name */
  userName?: string;
  onLogout?: () => void;
  onChangePassword?: () => void;
  /** Hide the bottom tab bar (e.g. when AIChat has its own nav) */
  hideTabBar?: boolean;
}

// Primary tabs for the bottom bar
const primaryTabs: { id: PageId; label: string; icon: typeof LayoutDashboard }[] = [
  { id: 'dashboard', label: '首页', icon: LayoutDashboard },
  { id: 'scores', label: '成绩', icon: TrendingUp },
  { id: 'recommendation', label: '志愿', icon: Star },
  { id: 'schools', label: '院校', icon: School },
];

// Secondary pages shown in the "More" bottom sheet
const moreItems: { id: PageId; label: string; icon: typeof MessageSquare }[] = [
  { id: 'ai-chat', label: 'AI对话', icon: MessageSquare },
  { id: 'majors', label: '专业探索', icon: BookOpen },
  { id: 'volunteer', label: '志愿模拟', icon: ClipboardList },
  { id: 'wrongbook', label: '错题本', icon: AlertTriangle },
  { id: 'students', label: '学生管理', icon: Users },
  { id: 'preferences', label: '偏好设置', icon: Settings },
  { id: 'data-admin', label: '数据管理', icon: Database },
  { id: 'llm-settings', label: 'AI设置', icon: Cpu },
];

const defaultPageTitles: Record<string, string> = {
  'dashboard': '首页',
  'schools': '院校探索',
  'recommendation': '志愿填报',
  'scores': '成绩轨迹',
  'ai-chat': 'AI对话',
  'majors': '专业探索',
  'volunteer': '志愿模拟',
  'wrongbook': '错题本',
  'students': '学生管理',
  'preferences': '偏好设置',
  'data-admin': '数据管理',
  'llm-settings': 'AI设置',
};

export default function MobileAppNav({
  currentPage,
  onNavigate,
  pageTitles,
  studentName,
  userName,
  onLogout,
  onChangePassword,
  hideTabBar = false,
}: MobileAppNavProps) {
  const [showMore, setShowMore] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const titles = { ...defaultPageTitles, ...pageTitles };

  // Lock body scroll when "More" sheet is open
  useEffect(() => {
    if (showMore) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [showMore]);

  const handleNavigate = (page: PageId) => {
    hapticLight();
    onNavigate(page);
    setShowMore(false);
    setShowUserMenu(false);
  };

  // Check if current page is one of the primary tabs
  const isMoreActive = moreItems.some(item => item.id === currentPage);

  return (
    <>
      {/* ===== Top Header Bar ===== */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 touch-manipulation">
        <div className="flex items-center justify-between px-3 py-2.5">
          {/* Left: page title */}
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold text-gray-800 truncate">
              {titles[currentPage] || '升学规划助手'}
            </h1>
            {studentName && (
              <p className="text-xs text-gray-500 truncate">{studentName}</p>
            )}
          </div>

          {/* Right: user avatar */}
          {userName && (
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                aria-label="用户菜单"
              >
                {userName[0]}
              </button>
              {showUserMenu && (
                <div className="absolute top-full right-0 mt-1 w-40 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                  {onChangePassword && (
                    <button
                      onClick={() => { onChangePassword(); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-blue-50 text-left text-gray-700 text-sm"
                    >
                      <KeyRound size={14} /> 修改密码
                    </button>
                  )}
                  {onLogout && (
                    <button
                      onClick={() => { onLogout(); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-red-50 text-left text-red-600 text-sm border-t border-gray-100"
                    >
                      <LogOut size={14} /> 退出登录
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* ===== Bottom Tab Bar ===== */}
      {!hideTabBar && (
        <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-1px_3px_rgba(0,0,0,0.05)] pb-safe">
          <div className="flex items-center justify-around py-1.5">
            {primaryTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = currentPage === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleNavigate(tab.id)}
                  className={`flex flex-col items-center justify-center py-1 px-3 min-w-[56px] min-h-[44px] active:scale-95 transition-all duration-150 touch-manipulation ${
                    isActive ? 'text-blue-600' : 'text-gray-400'
                  }`}
                  aria-label={tab.label}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon size={20} />
                  <span className={`text-[10px] mt-0.5 ${isActive ? 'font-medium text-blue-600' : 'text-gray-400'}`}>
                    {tab.label}
                  </span>
                </button>
              );
            })}

            {/* "More" tab */}
            <button
              onClick={() => setShowMore(!showMore)}
              className={`flex flex-col items-center justify-center py-1 px-3 min-w-[56px] min-h-[44px] active:scale-95 transition-all duration-150 touch-manipulation ${
                isMoreActive || showMore ? 'text-blue-600' : 'text-gray-400'
              }`}
              aria-label="更多"
            >
              <MoreHorizontal size={20} />
              <span className={`text-[10px] mt-0.5 ${isMoreActive || showMore ? 'font-medium text-blue-600' : 'text-gray-400'}`}>
                更多
              </span>
            </button>
          </div>
        </nav>
      )}

      {/* ===== "More" Bottom Sheet ===== */}
      <AnimatePresence>
        {showMore && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/40 z-[60]"
              onClick={() => setShowMore(false)}
            />

            {/* Sheet */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-2xl shadow-2xl pb-safe"
            >
              {/* Handle bar */}
              <div className="flex justify-center py-3">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-4 pb-3">
                <h2 className="text-lg font-semibold text-gray-800">更多功能</h2>
                <button
                  onClick={() => setShowMore(false)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                  aria-label="关闭"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Grid of items */}
              <div className="grid grid-cols-4 gap-1 px-4 pb-6">
                {moreItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentPage === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavigate(item.id)}
                      className={`flex flex-col items-center justify-center py-3 px-1 rounded-xl min-h-[72px] active:scale-95 transition-all duration-150 touch-manipulation ${
                        isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon size={22} />
                      <span className={`text-xs mt-1.5 ${isActive ? 'font-medium' : ''}`}>
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/**
 * Guest version of MobileAppNav — only shows public pages
 */
export function GuestMobileAppNav({
  currentPage,
  onNavigate,
}: {
  currentPage: PageId;
  onNavigate: (page: PageId) => void;
}) {
  const guestTabs: { id: PageId; label: string; icon: typeof MessageSquare }[] = [
    { id: 'ai-chat', label: 'AI对话', icon: MessageSquare },
    { id: 'schools', label: '院校探索', icon: School },
    { id: 'majors', label: '专业探索', icon: BookOpen },
  ];

  const guestTitles: Record<string, string> = {
    'ai-chat': 'AI对话',
    'schools': '院校探索',
    'majors': '专业探索',
  };

  return (
    <>
      {/* Top Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 touch-manipulation">
        <div className="flex items-center justify-between px-3 py-2.5">
          <h1 className="text-base font-semibold text-gray-800 truncate">
            {guestTitles[currentPage] || '升学规划助手'}
          </h1>
          <button
            onClick={() => onNavigate('login' as PageId)}
            className="text-sm text-blue-600 font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50"
          >
            登录
          </button>
        </div>
      </header>

      {/* Bottom Tab Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-1px_3px_rgba(0,0,0,0.05)] pb-safe">
        <div className="flex items-center justify-around py-1.5">
          {guestTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = currentPage === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onNavigate(tab.id)}
                className={`flex flex-col items-center justify-center py-1 px-3 min-w-[56px] min-h-[44px] active:scale-95 transition-all duration-150 touch-manipulation ${
                  isActive ? 'text-blue-600' : 'text-gray-400'
                }`}
                aria-label={tab.label}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon size={20} />
                <span className={`text-[10px] mt-0.5 ${isActive ? 'font-medium text-blue-600' : 'text-gray-400'}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}
